import Grid from '@mui/material/Grid2';
import { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../../Contexts/APIContext';
import DataContainer from '../../../Utilities/DataContainer';
import TransportTypesViewModel from '../../ViewModels/TransportTypesViewModel';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface IProps {
    id: string;
    selectedType: number;
    setSelectedType: (transportId: number) => void;
    surveyType: number;
    readOnly: boolean;
    date: string;
}

export default function TransportTypes({ id, selectedType, setSelectedType, surveyType, readOnly, date }: IProps) {
    const theme = useTheme();
    const { get, showMessage } = useContext(APIContext);
    const [transportTypes, setTransportTypes] = useState<TransportTypesViewModel[]>([]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [transportToDelete, setTransportToDelete] = useState<TransportTypesViewModel | null>(null);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [transportToAdd, setTransportToAdd] = useState<TransportTypesViewModel | null>(null);

    const loadTransportTypes = () => {
        if (!date) {
            return;
        }

        get(
            'SurveyDays',
            `GetTransportTypes?id=${id}&surveyType=${surveyType}&selectedDate=${date}`,
            (value: TransportTypesViewModel[]) => {
                setTransportTypes(value);
                setSelectedType(0);
            },
        );
    };

    useEffect(() => {
        let cancelled = false;
        setTransportTypes([]);

        if (!date) {
            return;
        }

        get(
            'SurveyDays',
            `GetTransportTypes?id=${id}&surveyType=${surveyType}&selectedDate=${date}`,
            (value: TransportTypesViewModel[]) => {
                if (cancelled) {
                    return;
                }

                setTransportTypes(value);
                setSelectedType(0);
            },
        );

        return () => {
            cancelled = true;
        };
    }, [id, date, surveyType]);

    const handleOpenDeleteDialog = (event: React.MouseEvent, mode: TransportTypesViewModel) => {
        event.stopPropagation();
        setTransportToDelete(mode);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setTransportToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (!transportToDelete) {
            return;
        }

        get(
            'SurveyDays',
            `DeleteTransportType?id=${id}&date=${date}&vehicleType=${transportToDelete.id}`,
            () => {
                showMessage('success', 'Transport type deleted');
                handleCloseDeleteDialog();
                loadTransportTypes();
            },
        );
    };

    const handleOpenAddDialog = (event: React.MouseEvent, mode: TransportTypesViewModel) => {
        event.stopPropagation();
        setTransportToAdd(mode);
        setAddDialogOpen(true);
    };

    const handleCloseAddDialog = () => {
        setAddDialogOpen(false);
        setTransportToAdd(null);
    };

    const handleConfirmAdd = () => {
        if (!transportToAdd) {
            return;
        }

        // TODO: update endpoint/params for your backend
        get(
            'SurveyDays',
            `AddTransportType?id=${id}&date=${date}&vehicleType=${transportToAdd.id}`,
            () => {
                showMessage('success', 'Transport type added');
                handleCloseAddDialog();
                loadTransportTypes();
            },
        );
    };

    return (
        <Grid container size={12} alignItems="flex-start" spacing={0}>
            <DataContainer title={'Mode of Transport'}>
                {transportTypes.map((mode) => {
                    const isDisabled = !mode.hasData;

                    return (
                        <Grid size={12} key={mode.id}>
                            <MenuItem
                                key={mode.id}
                                value={mode.id}
                                onClick={() => {
                                    if (isDisabled) {
                                        return;
                                    }
                                    setSelectedType(mode.id);
                                }}
                                selected={selectedType === mode.id}
                                sx={{
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    '&:hover': isDisabled
                                        ? {
                                            backgroundColor: 'transparent',
                                        }
                                        : undefined,
                                }}
                            >
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        marginRight: '8px',
                                        bgcolor: mode.hasData
                                            ? selectedType === mode.id
                                                ? theme.palette.primary.main
                                                : 'gray'
                                            : 'gray',
                                    }}
                                    src={`/Icons/${mode.icon}`}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
                                    }}
                                >
                                    {mode.description}
                                </Typography>
                                <Box flexGrow={1} />
                                {!readOnly && mode.hasData && (
                                    <IconButton
                                        size="small"
                                        edge="end"
                                        onClick={(event) => handleOpenDeleteDialog(event, mode)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {!readOnly && !mode.hasData && (
                                    <IconButton
                                        size="small"
                                        edge="end"
                                        onClick={(event) => handleOpenAddDialog(event, mode)}
                                    >
                                        <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </MenuItem>
                        </Grid>
                    );
                })}
            </DataContainer>

            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Delete transport type</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete{' '}
                        {transportToDelete ? `"${transportToDelete.description}"` : 'this transport type'}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} fullWidth maxWidth="xs">
                <DialogTitle>Add transport type</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to add{' '}
                        {transportToAdd ? `"${transportToAdd.description}"` : 'this transport type'}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog}>Cancel</Button>
                    <Button onClick={handleConfirmAdd} variant="contained">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
